import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import type { ThemeSentimentBreakdown } from '../../../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';

export interface ThemeBreakupItem {
  themeId: string;
  themeName: string;
  positive: number;
  neutral: number;
  negative: number;
  positiveCount: number;
  neutralCount: number;
  negativeCount: number;
}

@Component({
  selector: 'app-sentiment-details-theme-breakup-bars',
  standalone: false,
  templateUrl: './details-theme-breakup-bars.component.html',
  styleUrls: ['./details-theme-breakup-bars.component.scss'],
})
export class SentimentDetailsThemeBreakupBarsComponent implements OnChanges {
  @Input() themeBreakdown: ThemeSentimentBreakdown[] = [];
  /** When set, the matching theme bar is shown as selected (e.g. after bar click). */
  @Input() selectedThemeId: string | null = null;
  /** Emitted when the info icon is clicked – open side panel only. */
  @Output() themeInfoClick = new EventEmitter<ThemeBreakupItem>();
  /** Emitted when the bar is clicked – sync theme to Sub-theme break-up and Trend/Cohort cards only. */
  @Output() themeBarClick = new EventEmitter<ThemeBreakupItem>();
  /** Emitted when the Suggested toggle hides the currently selected theme – parent should clear selection. */
  @Output() selectedThemeHiddenByFilter = new EventEmitter<void>();

  /** When true, list only suggested themes (`isSuggestedTheme === true`); when false, only non-suggested. */
  showSuggestedThemesOnly = false;

  /**
   * Cached rows for `*ngFor` — avoids rebuilding a new array every CD cycle (which reset scroll on the list).
   */
  themesRows: ThemeBreakupItem[] = [];

  get hasAnySuggestedTheme(): boolean {
    return (this.themeBreakdown || []).some((t) => t.isSuggestedTheme === true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['themeBreakdown'] && !this.hasAnySuggestedTheme && this.showSuggestedThemesOnly) {
      this.showSuggestedThemesOnly = false;
    }
    if (changes['themeBreakdown']) {
      this.rebuildThemesRows();
    }
  }

  private filteredThemeBreakdown(): ThemeSentimentBreakdown[] {
    const rows = this.themeBreakdown || [];
    return rows.filter((t) =>
      this.showSuggestedThemesOnly ? t.isSuggestedTheme === true : t.isSuggestedTheme !== true,
    );
  }

  private rebuildThemesRows(): void {
    this.themesRows = this.filteredThemeBreakdown().map((t) => {
      const p = t.sentiment?.positive?.count ?? 0;
      const n = t.sentiment?.neutral?.count ?? 0;
      const neg = t.sentiment?.negative?.count ?? 0;
      const total = p + n + neg || 1;
      return {
        themeId: t.themeId,
        themeName: t.themeName,
        positive: (p / total) * 100,
        neutral: (n / total) * 100,
        negative: (neg / total) * 100,
        positiveCount: p,
        neutralCount: n,
        negativeCount: neg,
      };
    });
  }

  trackByThemeId(_index: number, t: ThemeBreakupItem): string {
    return t.themeId;
  }

  onSuggestedThemesToggle(): void {
    this.showSuggestedThemesOnly = !this.showSuggestedThemesOnly;
    this.rebuildThemesRows();
    const visible = new Set(this.themesRows.map((t) => t.themeId));
    if (this.selectedThemeId && !visible.has(this.selectedThemeId)) {
      this.selectedThemeHiddenByFilter.emit();
    }
  }

  onThemeInfoClick(theme: ThemeBreakupItem): void {
    this.themeInfoClick.emit(theme);
  }

  onThemeBarClick(theme: ThemeBreakupItem): void {
    this.themeBarClick.emit(theme);
  }
}
